import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUpRight,
  FiChevronDown,
  FiGlobe,
  FiGrid,
  FiMapPin,
  FiMessageSquare,
  FiShield,
  FiTarget,
  FiTool,
  FiTrendingUp,
} from "react-icons/fi";
import { FaQuoteLeft } from "react-icons/fa";
import { CalibrationPointIcon } from "../utils/calibrationIcons";

const sectionDetails = [
  {
    label: "Precision",
    icon: FiTarget,
    image: "/img/section-precision-premium.jpg",
    alt: "Precision dimensional calibration instrument and reference standards",
    caption: "Measurement certainty engineered into every result",
  },
  {
    label: "Capabilities",
    icon: FiTool,
    image: "/img/section-capabilities-premium.jpg",
    alt: "Electrical, pressure, dimensional, mass and thermal calibration instruments",
    caption: "Multi-discipline capability under one accredited partner",
  },
  {
    label: "Assurance",
    icon: FiShield,
    image: "/img/section-assurance-premium.jpg",
    alt: "Traceable calibration reference path and protected precision instrument",
    caption: "Traceability, uncertainty and technical confidence",
  },
  {
    label: "Industries",
    icon: FiGrid,
    image: "/img/section-industries-premium.jpg",
    alt: "Manufacturing, pharmaceutical, automotive, energy and laboratory industries",
    caption: "Specialist measurement support across critical industries",
  },
  {
    label: "Performance",
    icon: FiTrendingUp,
    image: "/img/section-assurance-premium.jpg",
    alt: "Verified calibration performance across traceable reference standards",
    caption: "Reliable instruments. Controlled processes. Better decisions.",
  },
  {
    label: "Local Support",
    icon: FiMapPin,
    image: "/img/section-nationwide-premium.jpg",
    alt: "Calibration service network operating from Pune across India",
    caption: "Responsive laboratory and eligible on-site support",
  },
  {
    label: "Nationwide Reach",
    icon: FiGlobe,
    image: "/img/section-nationwide-premium.jpg",
    alt: "Nationwide calibration service connections across India",
    caption: "Consistent calibration support wherever operations are located",
  },
  {
    label: "Partnership",
    icon: FiMessageSquare,
    image: "/img/section-partnership-premium.jpg",
    alt: "Complementary calibration systems aligned around a shared reference standard",
    caption: "A long-term technical partnership built around your requirements",
  },
];

const ItemGrid = ({ items, compact = false }) => (
  <ul className={`recon-data-list ${compact ? "recon-data-list--compact" : ""}`}>
    {items.map((item, index) => (
      <li key={`${item}-${index}`}>
        <span className="recon-data-index" aria-hidden="true">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="recon-data-check" aria-hidden="true">
          <CalibrationPointIcon label={item} />
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ProcessGrid = ({ steps }) => (
  <ol className="recon-process-flow">
    {steps.map((step, index) => (
      <li key={step.title}>
        <div className="recon-process-marker">
          <span>{String(index + 1).padStart(2, "0")}</span>
        </div>
        <div>
          <h3>{step.title}</h3>
          <p>{step.text}</p>
        </div>
      </li>
    ))}
  </ol>
);

const GroupedItems = ({ groups }) => (
  <div className="recon-group-grid">
    {groups.map((group, index) => (
      <article key={group.title} className="recon-group-panel">
        <div className="recon-group-heading">
          <span>{String(index + 1).padStart(2, "0")}</span>
          <h3>{group.title}</h3>
        </div>
        <ItemGrid items={group.items} compact />
      </article>
    ))}
  </div>
);

const VisualFrame = ({ detail, index }) => (
  <figure className="recon-section-visual">
    <img src={detail.image} alt={detail.alt} loading="lazy" />
    <div className="recon-visual-shade" />
    <span className="recon-visual-number" aria-hidden="true">
      {String(index + 1).padStart(2, "0")}
    </span>
    <figcaption>
      <span className="recon-visual-pulse" aria-hidden="true" />
      {detail.caption}
    </figcaption>
  </figure>
);

const SectionCopy = ({ section, detail, index }) => {
  const SectionIcon = detail.icon;

  return (
    <header className="recon-section-copy">
      <div className="recon-section-label">
        <span>
          <SectionIcon aria-hidden="true" />
        </span>
        {detail.label}
        <b>{String(index + 1).padStart(2, "0")}</b>
      </div>
      <h2>{section.title}</h2>
      <div className="recon-title-rule" aria-hidden="true">
        <span />
        <span />
      </div>
      {section.paragraphs?.map((paragraph, paragraphIndex) => (
        <p
          key={paragraph}
          className={paragraphIndex === 0 ? "recon-section-lead" : ""}
        >
          {paragraph}
        </p>
      ))}
    </header>
  );
};

const SectionData = ({ section }) => {
  if (
    !section.items &&
    !section.groups &&
    !section.steps &&
    !section.footer
  ) {
    return <div className="recon-section-data recon-section-data--empty" />;
  }

  return (
    <div className="recon-section-data">
      {section.items && <ItemGrid items={section.items} />}
      {section.groups && <GroupedItems groups={section.groups} />}
      {section.steps && <ProcessGrid steps={section.steps} />}
      {section.footer && (
        <p className="recon-section-footer">{section.footer}</p>
      )}
    </div>
  );
};

const ContactPanel = ({ section }) => (
  <section className="recon-contact-stage px-4 py-16 md:py-24">
    <div className="recon-contact-shell mx-auto max-w-7xl">
      <figure>
        <img
          src="/img/section-partnership-premium.jpg"
          alt="Calibration systems aligned around a shared reference standard"
          loading="lazy"
        />
        <div className="recon-contact-image-overlay" />
        <figcaption>
          <span>Shared standards</span>
          <strong>One dependable calibration partner</strong>
        </figcaption>
      </figure>
      <div className="recon-contact-copy">
        <div className="recon-section-label recon-section-label--dark">
          <span>
            <FiMessageSquare aria-hidden="true" />
          </span>
          Calibration partnership
        </div>
        <h2>{section.title}</h2>
        {section.paragraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {section.footer && <p>{section.footer}</p>}
        <Link to="/contact" className="recon-primary-button">
          Discuss Your Requirements
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);

const HomeLabel = ({ icon: Icon, children, dark = false }) => (
  <div
    className={`recon-section-label ${dark ? "recon-section-label--dark" : ""}`}
  >
    <span>
      <Icon aria-hidden="true" />
    </span>
    {children}
  </div>
);

const HomeParagraphs = ({ section, dark = false }) => (
  <div className={`home-premium-paragraphs ${dark ? "is-dark" : ""}`}>
    {section.paragraphs?.map((paragraph, index) => (
      <p key={paragraph} className={index === 0 ? "is-lead" : ""}>
        {paragraph}
      </p>
    ))}
    {section.footer && <p>{section.footer}</p>}
  </div>
);

const HomeProcess = ({ steps }) => (
  <ol className="home-process-list">
    {steps.map((step, index) => (
      <li key={step.title}>
        <span>{String(index + 1).padStart(2, "0")}</span>
        <div>
          <h3>{step.title}</h3>
          <p>{step.text}</p>
        </div>
      </li>
    ))}
  </ol>
);

export const HomeLongFormSections = ({ sections }) => {
  const [
    services,
    localSupport,
    nationwideSupport,
    reasons,
    accreditation,
    process,
    industries,
    quality,
    partnership,
  ] = sections;

  return (
    <div className="home-premium-flow">
      <section className="home-premium-services">
        <div className="home-premium-container">
          <div className="home-services-feature">
            <div className="home-services-copy">
              <HomeLabel icon={FiTarget}>Precision & capabilities</HomeLabel>
              <h2>{services.title}</h2>
              <HomeParagraphs section={services} />
            </div>
            <figure className="home-premium-image">
              <img
                src="/img/section-capabilities-premium.jpg"
                alt="Recon multi-discipline calibration capability"
                loading="lazy"
              />
              <div className="recon-visual-shade" />
              <figcaption>One accredited partner. Multiple disciplines.</figcaption>
            </figure>
          </div>

          <div className="home-services-list">
            <ItemGrid items={services.items} />
          </div>
        </div>
      </section>

      <section className="home-premium-reach">
        <div className="home-premium-container">
          <div className="home-reach-header">
            <HomeLabel icon={FiMapPin}>Local support & nationwide reach</HomeLabel>
            <h2>Calibration support designed around where you operate</h2>
          </div>

          <div className="home-reach-grid">
            <article className="home-reach-visual">
              <img
                src="/img/section-nationwide-premium.jpg"
                alt="Recon calibration support network from Pune across India"
                loading="lazy"
              />
              <div className="home-reach-overlay" />
              <div className="home-reach-visual-copy">
                <span>From Pune to PAN India</span>
                <strong>One quality system across every service location</strong>
              </div>
            </article>

            <div className="home-reach-copy-grid">
              <article>
                <span className="home-card-number">01</span>
                <h3>{localSupport.title}</h3>
                <HomeParagraphs section={localSupport} />
              </article>
              <article>
                <span className="home-card-number">02</span>
                <h3>{nationwideSupport.title}</h3>
                <HomeParagraphs section={nationwideSupport} />
              </article>
            </div>
          </div>

        </div>
      </section>

      <section className="home-premium-reasons">
        <div className="home-premium-container">
          <div className="home-reasons-stage">
            <div className="home-reasons-copy">
              <HomeLabel icon={FiShield} dark>
                Assurance
              </HomeLabel>
              <h2>{reasons.title}</h2>
              <HomeParagraphs section={reasons} dark />
            </div>

            <figure className="home-reasons-visual">
              <img
                src="/img/section-assurance-premium.jpg"
                alt="Accredited calibration assurance and traceability"
                loading="lazy"
              />
              <div className="recon-visual-shade" />
              <figcaption>Accredited. Traceable. Dependable.</figcaption>
            </figure>
          </div>

          <div className="home-reasons-list">
            <ItemGrid items={reasons.items} />
          </div>
        </div>
      </section>

      <section className="home-premium-assurance">
        <div className="home-premium-container home-assurance-grid">
          <article className="home-accreditation-card">
            <img
              src="/img/section-assurance-premium.jpg"
              alt="Traceable accredited calibration assurance"
              loading="lazy"
            />
            <div className="home-accreditation-overlay" />
            <div className="home-accreditation-copy">
              <HomeLabel icon={FiShield} dark>
                Accredited assurance
              </HomeLabel>
              <h2>{accreditation.title}</h2>
              <HomeParagraphs section={accreditation} dark />
            </div>
          </article>

          <article className="home-process-card">
            <HomeLabel icon={FiTrendingUp}>Performance</HomeLabel>
            <h2>{process.title}</h2>
            <HomeProcess steps={process.steps} />
          </article>
        </div>
      </section>

      <section className="home-premium-industries">
        <div className="home-premium-container">
          <div className="home-industries-header">
            <HomeLabel icon={FiGrid}>Industries</HomeLabel>
            <h2>{industries.title}</h2>
            <HomeParagraphs section={industries} />
          </div>

          <figure className="home-industries-stage">
            <img
              src="/img/section-industries-premium.jpg"
              alt="Industries supported by Recon calibration services"
              loading="lazy"
            />
            <div className="home-industries-overlay" />
            <figcaption>Precision that performs across critical industries.</figcaption>
          </figure>

          <div className="home-industries-list">
            <ItemGrid items={industries.items} />
          </div>

          <article className="home-quality-panel">
            <div className="home-quality-mark" aria-hidden="true">
              <FiTarget />
            </div>
            <div>
              <HomeLabel icon={FiShield}>Quality commitment</HomeLabel>
              <h2>{quality.title}</h2>
              <HomeParagraphs section={quality} />
            </div>
          </article>
        </div>
      </section>

      <ContactPanel section={partnership} />
    </div>
  );
};

export const LongFormSections = ({ sections }) => (
  <div className="recon-editorial-flow">
    {sections.map((section, index) => {
      if (section.cta) {
        return <ContactPanel key={section.title} section={section} />;
      }

      const detail = sectionDetails[index % sectionDetails.length];
      const layout = index % 4;

      return (
        <section
          key={section.title}
          className={`recon-editorial-section recon-layout-${layout} ${
            layout === 2 ? "recon-editorial-section--dark" : ""
          }`}
        >
          <div className="recon-editorial-shell">
            <SectionCopy section={section} detail={detail} index={index} />
            <VisualFrame detail={detail} index={index} />
            <SectionData section={section} />
          </div>
        </section>
      );
    })}
  </div>
);

export const FaqSection = ({ faqs }) => (
  <section className="recon-knowledge-section" aria-labelledby="faq-heading">
    <div className="recon-knowledge-shell">
      <aside className="recon-knowledge-card">
        <figure>
          <img
            src="/img/section-knowledge-premium.jpg"
            alt="Calibration reference book, instrument and measurement standards"
            loading="lazy"
          />
          <div className="recon-visual-shade" />
          <figcaption>Technical knowledge, clearly explained</figcaption>
        </figure>
        <div className="recon-knowledge-copy">
          <div className="recon-section-label">
            <span>
              <FiMessageSquare aria-hidden="true" />
            </span>
            Knowledge centre
          </div>
          <h2 id="faq-heading">Frequently Asked Questions</h2>
          <p>
            Clear answers about our accredited calibration process, service
            coverage, certificates, and turnaround support.
          </p>
          <Link to="/contact">
            Ask our calibration team
            <FiArrowUpRight aria-hidden="true" />
          </Link>
        </div>
      </aside>

      <div className="recon-faq-list">
        {faqs.map((faq, index) => (
          <details key={faq.question} className="recon-faq-item">
            <summary>
              <span className="recon-faq-number">
                {String(index + 1).padStart(2, "0")}
              </span>
              <strong>{faq.question}</strong>
              <span className="recon-faq-toggle">
                <FiChevronDown aria-hidden="true" />
              </span>
            </summary>
            <div>
              <p>{faq.answer}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const TestimonialsSection = ({ testimonials }) => (
  <section className="recon-testimonials px-4 py-16 text-white md:py-24">
    <div className="relative mx-auto max-w-6xl">
      <div className="mx-auto max-w-3xl text-center">
        <div className="recon-section-label recon-section-label--dark mx-auto">
          Client confidence
        </div>
        <h2 className="mt-5 text-3xl font-bold tracking-tight md:text-4xl">
          Trusted where precision matters
        </h2>
        <p className="mx-auto mt-4 max-w-2xl leading-7 text-slate-300">
          Long-term calibration partnerships built on reliable results,
          responsive support, and complete traceability.
        </p>
      </div>

      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {testimonials.map((testimonial, index) => (
          <figure key={testimonial.attribution} className="recon-quote-card">
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#65d3df]/15 text-xl text-[#65d3df]">
                <FaQuoteLeft aria-hidden="true" />
              </span>
              <span className="text-xs font-bold tracking-[0.2em] text-white/35">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <blockquote className="mt-7 leading-7 text-slate-100">
              “{testimonial.quote}”
            </blockquote>
            <figcaption className="mt-7 border-t border-white/10 pt-5 text-sm font-bold text-[#65d3df]">
              {testimonial.attribution}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);
